import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import AthleteSeasonPointsTable from './AthleteSeasonPointsTable';
import PropTypes from 'prop-types';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import { getAthleteSeasonPoints } from 'api/reportsApi';
import { SORT_BY, sortByGroupGenderName } from 'utils/athleteOrder';
import { clubFeaturesOf } from 'utils/clubFeatures';
import {
  activeTeamFilter,
  matchesTeamFilter,
  teamFilterOptions,
  teamWordsOf,
  teamsOfRows,
} from 'utils/teamLabel';

// The most recent calculatedAt across every row the API returned, as its original ISO string, or
// null for an empty list. The season replay stamps only the rows whose totals changed, so the latest
// stamp is the last time any athlete's season points were updated. The first row alone would only
// track the leading athlete, which goes stale whenever they sit out a meet.
const latestCalculatedAt = (rows) => {
  let latest = null;
  let latestMs = -Infinity;
  for (const row of rows) {
    const ms = Date.parse(row.calculatedAt);
    if (!Number.isNaN(ms) && ms > latestMs) {
      latestMs = ms;
      latest = row.calculatedAt;
    }
  }
  return latest;
};

const AthleteSeasonPoints = ({ clubID, onDataChange, recalculationTrigger }) => {
  const storageKey = `athleteSeasonPointsFilters_${clubID}`;

  // Load saved filters from SessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedFilters = getSavedFilters();

  const [athletePoints, setAthletePoints] = useState([]);
  const [filteredPoints, setFilteredPoints] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [minPRsBeaten, setMinPRsBeaten] = useState(savedFilters?.minPRsBeaten || 0);
  // Sort by: total points, as the server lists them, or the athlete's group.
  const [sortBy, setSortBy] = useState(
    savedFilters?.sortBy === SORT_BY.GROUP ? SORT_BY.GROUP : SORT_BY.POINTS
  );
  const [loading, setLoading] = useState(true);
  const [lastCalculatedAt, setLastCalculatedAt] = useState(null);

  // A school's House column and filter (points slice 5), named in its word. Each row carries the athlete's house
  // now (`team`), so the filter's houses come from the rows and the page makes no request for them. Ticked houses
  // are OR'd together, as on Club Athletes; the files are not narrowed by it, as by the other filters.
  const { selectedClub } = useSelector((state) => state.user || {});
  const hasTeams = clubFeaturesOf(selectedClub).teams;
  const teamWords = teamWordsOf(selectedClub);
  const [teamTicked, setTeamTicked] = useState(savedFilters?.team || []);
  const houses = useMemo(
    () => (hasTeams ? teamsOfRows(athletePoints, (p) => p.team) : []),
    [hasTeams, athletePoints]
  );
  const teamOptions = useMemo(
    () => (hasTeams ? teamFilterOptions(houses, teamWords) : []),
    [hasTeams, houses, teamWords]
  );
  const teamFilter = useMemo(() => activeTeamFilter(teamTicked, teamOptions), [teamTicked, teamOptions]);

  // Save filters to SessionStorage whenever they change
  useEffect(() => {
    const filters = {
      minPRsBeaten,
      sortBy,
      team: teamTicked,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [minPRsBeaten, sortBy, teamTicked, storageKey]);

  const fetchAthletePoints = useCallback(async () => {
    try {
      if (clubID) {
        setLoading(true);
        const response = await getAthleteSeasonPoints(clubID);
        const pointsData = response.data || [];
        setAthletePoints(pointsData);
        
        // Taken from the full response, before the client-side filters, so hiding athletes never moves it
        setLastCalculatedAt(latestCalculatedAt(pointsData));
      }
      setLoading(false);
    } catch (err) {
      console.error('[AthletePoints] Fetch athlete points error: ', err);
      toast.error('Failed to load athlete season points');
      setLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    if (clubID) {
      fetchAthletePoints();
    }
  }, [fetchAthletePoints, clubID, recalculationTrigger]);

  useEffect(() => {
    if (athletePoints?.length) {
      let results = athletePoints;
      
      // Apply search filter
      if (searchText) {
        results = results.filter((p) => {
          const fullName = `${p.athleteFirstName} ${p.athleteLastName}`.toLowerCase();
          return fullName.includes(searchText.toLowerCase());
        });
      }
      
      // Apply minimum PRs beaten filter
      if (minPRsBeaten > 0) {
        results = results.filter((p) => p.personalBestsBeaten >= minPRsBeaten);
      }

      // A school's house filter
      if (teamFilter.length > 0) {
        results = results.filter((p) => matchesTeamFilter(p.team?.teamID, teamFilter, houses));
      }
      
      // The server lists athletes by total points. By group: youngest group first, then female before
      // male, then last name (utils/athleteOrder.js), the order the Season Best report uses.
      if (sortBy === SORT_BY.GROUP) {
        results = sortByGroupGenderName(results, {
          group: (p) => p.athleteAge,
          gender: (p) => p.athleteGender,
          lastName: (p) => p.athleteLastName,
          firstName: (p) => p.athleteFirstName,
        });
      }

      setFilteredPoints(results);
    } else {
      setFilteredPoints([]);
    }
  }, [searchText, athletePoints, minPRsBeaten, sortBy, teamFilter, houses]);

  // Notify parent component of data changes (for the export buttons). The two files are asked for in
  // the order chosen in Sort by.
  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredPoints.length > 0,
        sortBy,
      });
    }
  }, [filteredPoints, sortBy, onDataChange]);

  return (
    <>
      <LoadingContent loading={loading}>
        <AthleteSeasonPointsTable
          athletePoints={filteredPoints}
          setSearchText={setSearchText}
          minPRsBeaten={minPRsBeaten}
          setMinPRsBeaten={setMinPRsBeaten}
          sortBy={sortBy}
          setSortBy={setSortBy}
          lastCalculatedAt={lastCalculatedAt}
          teamWords={hasTeams ? teamWords : null}
          teamOptions={teamOptions}
          teamFilter={teamFilter}
          setTeamFilter={setTeamTicked}
        />
      </LoadingContent>
    </>
  );
};

AthleteSeasonPoints.propTypes = {
  clubID: PropTypes.string,
  onDataChange: PropTypes.func,
  recalculationTrigger: PropTypes.number,
};

export default AthleteSeasonPoints;